import { useState } from "react";
import { ChevronDown, ChevronRight, Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import type { CrmRecordType } from "@/lib/api/crm/masters";
import { useListBaseTraders, type BaseTrader } from "@/lib/api/trader";
import { recordTypeOrder, recordTypePluralLabels } from "../recordTypes";

type CrmListFiltersProps = {
  search: string;
  onSearchChange: (search: string) => void;
  /** Selected `types` URL values. */
  types: string[];
  /** Toggles one record type; `null` clears the record type filter. */
  onTypeToggle: (type: CrmRecordType | null) => void;
  /** Selected `trader` URL value (trader id); empty for all traders. */
  traderId: string;
  /** Sets the trader filter; empty string clears it. */
  onTraderChange: (traderId: string) => void;
};

/** Collapsible filter section of the CRM list: search, record type and trader filters. */
export function CrmListFilters({
  search,
  onSearchChange,
  types,
  onTypeToggle,
  traderId,
  onTraderChange,
}: CrmListFiltersProps) {
  const [isOpen, setIsOpen] = useState(true);
  const tradersResult = useListBaseTraders();
  const traders: BaseTrader[] = tradersResult.data?.success ? tradersResult.data.data : [];
  const traderOptions = [...traders]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((trader) => ({ value: trader.id, label: trader.name }));
  const failedToLoadTraders = tradersResult.isError || tradersResult.data?.success === false;
  const selectedTypes = recordTypeOrder.filter((type) => types.includes(type));
  const typeLabel = selectedTypes.length === 0
    ? "All record types"
    : selectedTypes.length === 1
      ? recordTypePluralLabels[selectedTypes[0]]
      : `${selectedTypes.length} record types`;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="rounded-lg border bg-white shadow-sm"
      data-testid="crm-list-filters"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700"
          data-testid="crm-list-filters-toggle"
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <SlidersHorizontal className="h-4 w-4" />
          Filters
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search by name"
              className="h-9 pl-8"
              data-testid="crm-list-search"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" data-testid="crm-list-type-filter">
                {typeLabel}
                <ChevronDown className="ml-2 h-4 w-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuCheckboxItem
                checked={selectedTypes.length === 0}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => onTypeToggle(null)}
                data-testid="crm-list-type-filter-all"
              >
                All record types
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              {recordTypeOrder.map((type) => (
                <DropdownMenuCheckboxItem
                  key={type}
                  checked={types.includes(type)}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={() => onTypeToggle(type)}
                  data-testid={`crm-list-type-filter-${type}`}
                >
                  {recordTypePluralLabels[type]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-1" data-testid="crm-list-trader-filter">
            <Combobox
              id="crm-list-trader-filter"
              dwimOnChange
              virtualizeList
              className="h-9 w-64"
              popoverClassName="w-64"
              placeholder={
                tradersResult.isLoading
                  ? "Loading traders…"
                  : failedToLoadTraders
                    ? "Couldn't load traders"
                    : "All traders"
              }
              options={traderOptions}
              value={traderId}
              onValueChange={onTraderChange}
              disabled={failedToLoadTraders}
            />
            {traderId !== "" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2"
                onClick={() => onTraderChange("")}
                title="Clear trader filter"
                data-testid="crm-list-trader-filter-clear"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
